import React, { useContext, useMemo, useState } from 'react';

import {
    Select,
    Checkbox,
    ListItemText,
    Box,
    MenuItem,
    Grid,
    Skeleton,
    Table,
    TableRow,
    TableCell,
    TableBody
} from '@mui/material';
import _ from 'lodash';
import Moment from 'moment';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useContainer } from 'unstated-next';

import ClassActivityTypeHeatmapUserRow from './ClassActivityTypeHeatmapUserRow';
import ClassSessionHeatmapUserRow from './ClassSessionHeatmapUserRow';
import ClassSessionsStudentToolbar from './ClassSessionsStudentToolbar';
import { StyledRoleSelect } from '../../../../../styles/input-row';
import AdminAuthContainer from '../../../AdminAuthContainer';
import { ClassSessionContext } from '../../../ClassSessionContext';

const Spacer = styled.div`
    margin: 1rem;
`;

const PaddedSkeleton = styled(Skeleton)`
    margin: 0.5rem 0;
`;

const ClassSessionHeatmap = styled(Box)`
    width: 100%;
`;

export const ClassSessionDetailTimeline = () => {
    const { t } = useTranslation('portal');
    const { classSessionData } = useContext(ClassSessionContext);
    const { authMode } = useContainer(AdminAuthContainer);
    console.log('ClassSessionDetailTimeline classSessionData', classSessionData, 'authMode', authMode);

    const [searchText, setSearchText] = useState('');
    const [sortMode, setSortMode] = useState(window.localStorage.getItem('sortMode') || 'alphabetical');
    const [actionFilter, setActionFilter] = useState<string[]>([
        'ATTENDANCE',
        'IS_TALKING',
        'PUBLIC_CHAT',
        'PRIVATE_CHAT',
        'RAISED_HAND',
        'FOCUSED',
        'FEEDBACK',
        'REACTION',
        'STARS',
        'ERROR'
    ]);
    console.log('actionFilter', actionFilter);
    const [showInactiveUsers, setShowInactiveUsers] = useState<boolean>(false);
    console.log('showInactiveUsers', showInactiveUsers);

    const filteredRoster = useMemo(
        () =>
            classSessionData.students?.filter((student) => {
                const includedInUserName = student.user_name
                    .toLocaleLowerCase()
                    .includes(searchText.toLocaleLowerCase());
                const includedInEmail = student.email?.toLocaleLowerCase().includes(searchText.toLocaleLowerCase());

                return includedInUserName || includedInEmail;
            }),
        [searchText, classSessionData.students]
    );

    const handleChangeSearch = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
        setSearchText(event.target.value);
    };

    const handleActionFilter = (action: string[]) => {
        setActionFilter(action);
    };
    const handleActionFilterChange = (event) => {
        console.log('handleActionFilterChange', event);
        handleActionFilter(event.target.value);
    };

    const handleShowInactiveUsers = (value: boolean) => {
        setShowInactiveUsers(value);
    };
    const handleShowInactiveUsersChange = (event) => {
        console.log('handleShowInactiveUsersChange', event);
        handleShowInactiveUsers(event.target.checked);
    };

    const handleSortMode = (val: string) => {
        setSortMode(val);
    };
    const handleSortModeChange = (event) => {
        window.localStorage.setItem('sortMode', event.target.value);
        handleSortMode(event.target.value);
    };

    const classActivityTitle = 'Class activity';
    const { classActivities } = classSessionData;

    const activeTeachersTitle = 'Instructor activity';
    const activeStudentsTitle = 'Student activity';
    const activeTeachers = filteredRoster!.filter(
        (student) =>
            (student.role === 'teacher' || student.role === 'assistant') &&
            (showInactiveUsers || (student.reportingEvents && student.reportingEvents.length > 0))
    );
    const activeStudents = filteredRoster!.filter(
        (student) =>
            student.role === 'student' &&
            (showInactiveUsers || (student.reportingEvents && student.reportingEvents.length > 0))
    );

    const sortedActiveTeachers = useMemo(
        () =>
            activeTeachers.sort((a, b) => {
                let userNameSortValue = 0;
                if (a.user_name.toLowerCase() > b.user_name.toLowerCase()) {
                    userNameSortValue = 1;
                } else if (a.user_name.toLowerCase() === b.user_name.toLowerCase()) {
                    userNameSortValue = 0;
                } else {
                    userNameSortValue = -1;
                }
                if (sortMode === 'alphabetical') {
                    return userNameSortValue;
                }
                if (sortMode === 'stars') {
                    return b.aggregates['Stars Count'] - a.aggregates['Stars Count'] || userNameSortValue;
                }
                if (sortMode === 'errors') {
                    const aErrorCount = a.reportingEvents.filter((event) => event.event_type === 'ERROR').length;
                    const bErrorCount = b.reportingEvents.filter((event) => event.event_type === 'ERROR').length;
                    return bErrorCount - aErrorCount || userNameSortValue;
                }
                return b.scores.totalScorePercentage - a.scores.totalScorePercentage || userNameSortValue;
            }),
        [activeTeachers, sortMode]
    );

    const sortedActiveStudents = useMemo(
        () =>
            activeStudents.sort((a, b) => {
                let userNameSortValue = 0;
                if (a.user_name.toLowerCase() > b.user_name.toLowerCase()) {
                    userNameSortValue = 1;
                } else if (a.user_name.toLowerCase() === b.user_name.toLowerCase()) {
                    userNameSortValue = 0;
                } else {
                    userNameSortValue = -1;
                }
                if (sortMode === 'alphabetical') {
                    return userNameSortValue;
                }
                if (sortMode === 'stars') {
                    return b.aggregates['Stars Count'] - a.aggregates['Stars Count'] || userNameSortValue;
                }
                if (sortMode === 'errors') {
                    const aErrorCount = a.reportingEvents.filter((event) => event.event_type === 'ERROR').length;
                    const bErrorCount = b.reportingEvents.filter((event) => event.event_type === 'ERROR').length;
                    return bErrorCount - aErrorCount || userNameSortValue;
                }
                return b.scores.totalScorePercentage - a.scores.totalScorePercentage || userNameSortValue;
            }),
        [activeStudents, sortMode]
    );

    const filterOptions = [
        {
            label: 'Attendance',
            value: 'ATTENDANCE'
        },
        {
            label: 'Talking',
            value: 'IS_TALKING'
        },
        {
            label: 'Public Chat',
            value: 'PUBLIC_CHAT'
        },
        {
            label: 'Private Chat',
            value: 'PRIVATE_CHAT'
        },
        {
            label: 'Raised hand',
            value: 'RAISED_HAND'
        },
        {
            label: 'Unfocused',
            value: 'FOCUSED'
        },
        {
            label: 'Feedback',
            value: 'FEEDBACK'
        },
        {
            label: 'Reaction',
            value: 'REACTION'
        },
        {
            label: 'Stars',
            value: 'STARS'
        },
        {
            label: 'Error',
            value: 'ERROR'
        }
    ];

    if (classSessionData.loading) {
        return (
            <>
                <Grid container justifyContent="space-between">
                    <Grid item>
                        <Skeleton width={300} height={60} />
                    </Grid>
                    <Grid item>
                        <Skeleton width={150} height={60} />
                    </Grid>
                </Grid>
                <Spacer />
                <PaddedSkeleton height={35} />
                <PaddedSkeleton height={35} />
                <PaddedSkeleton height={35} />
                <PaddedSkeleton height={35} />
                <PaddedSkeleton height={35} />
                <Spacer />
                <Grid container justifyContent="flex-end">
                    <Skeleton width={400} height={50} />
                </Grid>
            </>
        );
    }

    const { start_time: rawStartTime, end_time: rawEndTime } = classSessionData;
    const classDuration = Number(rawEndTime) - Number(rawStartTime);
    const startTime = Number(rawStartTime) - classDuration * 0.05;
    const endTime = Number(rawEndTime) + classDuration * 0.05;

    const startPercentage = 100 * Math.min(1, Math.max(0, (Number(rawStartTime) - startTime) / (endTime - startTime)));
    const endPercentage = 100 * Math.min(1, Math.max(0, (Number(rawEndTime) - startTime) / (endTime - startTime)));

    const eventColors = {
        Talking: 'RGB(165, 85, 70)',
        'Hand Raised': 'RGB(213,135,117)',
        Focused: 'RGB(240, 230, 215)',
        Attendance: 'RGB(230, 180, 155)'
    } as any;
    if (!actionFilter.includes('IS_TALKING')) {
        delete eventColors['Talking'];
    }
    if (!actionFilter.includes('RAISED_HAND')) {
        delete eventColors['Hand Raised'];
    }
    if (!actionFilter.includes('FOCUSED')) {
        delete eventColors['Focused'];
    }
    if (!actionFilter.includes('ATTENDANCE')) {
        delete eventColors['Attendance'];
    }

    return (
        <>
            <Table style={{ width: '100%', padding: '0px', margin: '0px' }}>
                <TableBody style={{ width: '100%', padding: '0px', margin: '0px' }}>
                    <TableRow style={{ width: '100%', padding: '0px', margin: '0px' }}>
                        <TableCell>
                            <ClassSessionsStudentToolbar
                                searchText={searchText}
                                handleChangeSearch={handleChangeSearch}
                            />
                        </TableCell>
                        <TableCell>
                            <Select
                                name="sortMode"
                                onChange={handleSortModeChange}
                                value={sortMode}
                                style={{ height: '42px' }}
                            >
                                <MenuItem value="alphabetical">{t('timeline-sort-alphabetical')}</MenuItem>
                                <MenuItem value="engagement">{t('timeline-sort-engagement')}</MenuItem>
                                <MenuItem value="stars">{t('timeline-sort-stars')}</MenuItem>
                                <MenuItem value="errors">{t('timeline-sort-errors')}</MenuItem>
                            </Select>
                        </TableCell>
                        <TableCell>
                            <StyledRoleSelect
                                name="roles"
                                id="demo-multiple-checkbox"
                                multiple
                                value={actionFilter}
                                onChange={(event) => handleActionFilterChange(event)}
                                renderValue={(selected) =>
                                    `${_.isArray(selected) ? selected.length : '0'} ${t('event-types')}`
                                }
                            >
                                {filterOptions.map((filterOption) => (
                                    <MenuItem key={filterOption.value} value={filterOption.value}>
                                        <Checkbox checked={actionFilter.indexOf(filterOption.value) > -1}></Checkbox>
                                        <ListItemText primary={t(filterOption.label)} />
                                    </MenuItem>
                                ))}
                            </StyledRoleSelect>
                        </TableCell>
                        <TableCell>
                            <ListItemText primary={t('show-inactive-users')} style={{ display: 'inline-block' }} />
                            <Checkbox
                                onChange={(event) => handleShowInactiveUsersChange(event)}
                                checked={showInactiveUsers}
                            ></Checkbox>
                        </TableCell>
                    </TableRow>
                </TableBody>
            </Table>
            <Spacer />
            {Object.keys(eventColors).map((eventType) => (
                <div
                    style={{
                        backgroundColor: eventColors[eventType],
                        padding: '4px',
                        display: 'inline-block',
                        marginRight: '8px',
                        fontWeight: 'bold'
                    }}
                >
                    {eventType}
                </div>
            ))}
            <ClassSessionHeatmap
                style={{
                    width: '100%',
                    position: 'relative',
                    marginTop: '20px'
                }}
            >
                <div style={{ position: 'relative' }}>
                    <h2>{classActivityTitle}</h2>
                    {_.values(classActivities!).map((classActivity, index) => (
                        <ClassActivityTypeHeatmapUserRow
                            classActivity={classActivity}
                            classSessionData={classSessionData}
                            key={index}
                            index={index}
                        ></ClassActivityTypeHeatmapUserRow>
                    ))}
                    <div
                        style={{
                            position: 'absolute',
                            left: '200px',
                            width: 'calc(100% - 200px)',
                            height: 'calc(100% - 50px)',
                            top: '50px'
                        }}
                    >
                        <div
                            id="startLine"
                            style={{
                                left: `${startPercentage}%`,
                                top: '0px',
                                height: '100%',
                                width: '1px',
                                position: 'absolute',
                                borderLeft: '1px dashed #000',
                                zIndex: '5'
                            }}
                        ></div>
                        <div
                            style={{
                                left: `${startPercentage}%`,
                                top: '-30px',
                                fontSize: '12px',
                                textAlign: 'center',
                                position: 'absolute',
                                zIndex: '5',
                                transform: 'translateX(-50%)',
                                lineHeight: '12px',
                                width: '100px'
                            }}
                        >
                            {t('session-start')}
                            <br />
                            {Moment(new Date(Number(rawStartTime) * 1000)).format('h:mm A')}
                        </div>
                        <div
                            style={{
                                left: `${endPercentage}%`,
                                top: '0px',
                                height: '100%',
                                width: '1px',
                                position: 'absolute',
                                borderLeft: '1px dashed #000',
                                zIndex: '5'
                            }}
                        ></div>
                        <div
                            style={{
                                left: `${endPercentage}%`,
                                top: '-30px',
                                fontSize: '12px',
                                textAlign: 'center',
                                position: 'absolute',
                                zIndex: '5',
                                transform: 'translateX(-50%)',
                                lineHeight: '12px',
                                width: '100px'
                            }}
                        >
                            {t('session-end')}
                            <br />
                            {Moment(new Date(Number(rawEndTime) * 1000)).format('h:mm A')}
                        </div>
                    </div>
                </div>
                <div style={{ position: 'relative' }}>
                    <h2>{activeTeachersTitle}</h2>
                    {sortedActiveTeachers!.map((student, index) => (
                        <ClassSessionHeatmapUserRow
                            actionFilter={actionFilter}
                            classSessionData={classSessionData}
                            student={student}
                            key={index}
                            index={index}
                        ></ClassSessionHeatmapUserRow>
                    ))}
                    <div
                        style={{
                            position: 'absolute',
                            left: '200px',
                            width: 'calc(100% - 200px)',
                            height: 'calc(100% - 45px)',
                            top: '45px'
                        }}
                    >
                        <div
                            id="startLine"
                            style={{
                                left: `${startPercentage}%`,
                                top: '0px',
                                height: '100%',
                                width: '1px',
                                position: 'absolute',
                                borderLeft: '1px dashed #000',
                                zIndex: '5'
                            }}
                        ></div>
                        <div
                            style={{
                                left: `${endPercentage}%`,
                                top: '0px',
                                height: '100%',
                                width: '1px',
                                position: 'absolute',
                                borderLeft: '1px dashed #000',
                                zIndex: '5'
                            }}
                        ></div>
                    </div>
                </div>
                <div style={{ position: 'relative' }}>
                    <h2>{activeStudentsTitle}</h2>
                    {sortedActiveStudents!.map((student, index) => (
                        <ClassSessionHeatmapUserRow
                            actionFilter={actionFilter}
                            classSessionData={classSessionData}
                            student={student}
                            key={index}
                            index={index}
                        ></ClassSessionHeatmapUserRow>
                    ))}
                    <div
                        style={{
                            position: 'absolute',
                            left: '200px',
                            width: 'calc(100% - 200px)',
                            height: 'calc(100% - 30px)',
                            top: '45px'
                        }}
                    >
                        <div
                            id="startLine"
                            style={{
                                left: `${startPercentage}%`,
                                top: '0px',
                                height: '100%',
                                width: '1px',
                                position: 'absolute',
                                borderLeft: '1px dashed #000',
                                zIndex: '5'
                            }}
                        ></div>
                        <div
                            style={{
                                left: `${startPercentage}%`,
                                top: 'calc(100% + 5px)',
                                fontSize: '12px',
                                textAlign: 'center',
                                position: 'absolute',
                                zIndex: '5',
                                transform: 'translateX(-50%)',
                                lineHeight: '12px',
                                width: '100px'
                            }}
                        >
                            {t('session-start')}
                            <br />
                            {Moment(new Date(Number(rawStartTime) * 1000)).format('h:mm A')}
                        </div>
                        <div
                            style={{
                                left: `${endPercentage}%`,
                                top: '0px',
                                height: '100%',
                                width: '1px',
                                position: 'absolute',
                                borderLeft: '1px dashed #000',
                                zIndex: '5'
                            }}
                        ></div>
                        <div
                            style={{
                                left: `${endPercentage}%`,
                                top: 'calc(100% + 5px)',
                                fontSize: '12px',
                                textAlign: 'center',
                                position: 'absolute',
                                zIndex: '5',
                                transform: 'translateX(-50%)',
                                lineHeight: '12px',
                                width: '100px'
                            }}
                        >
                            {t('session-end')}
                            <br />
                            {Moment(new Date(Number(rawEndTime) * 1000)).format('h:mm A')}
                        </div>
                    </div>
                </div>
            </ClassSessionHeatmap>
        </>
    );
};
